// src/components/layout/Footer.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Send,
  MessageCircle,
  Mail,
  Share2
} from 'lucide-react'
import { api } from '../../api/client'
import { endpoints } from '../../api/endpoints'
import logo from '../../assets/vite.svg'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [socialLinks, setSocialLinks] = useState([])
  const [loading, setLoading] = useState(true)

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'FAQ', href: '/faq' }
  ]

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Service', href: '/terms-of-service' },
    { name: 'Cookie Policy', href: '/cookies' },
    { name: 'Disclaimer', href: '/disclaimer' }
  ]

  // Social icon mapping with Lucide icons
  const socialIcons = {
    twitter: Twitter,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
    youtube: Youtube,
    telegram: Send,
    whatsapp: MessageCircle,
    default: Share2
  }

  // Helper function to extract array from response
  const extractSocialLinks = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    if (data && Array.isArray(data.socialLinks)) return data.socialLinks;
    if (data && Array.isArray(data.links)) return data.links;
    if (data && typeof data === 'object') return [data];
    return [];
  }

  // Fetch social links from backend
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        setLoading(true)
        const response = await api.get(endpoints.public.socialLinks)
        const linksData = extractSocialLinks(response.data)
        
        const transformedLinks = linksData.map(link => {
          const platform = link.platform?.toLowerCase() || 'unknown'
          const IconComponent = socialIcons[platform] || socialIcons.default
          
          return {
            id: link.id,
            name: link.platform?.charAt(0).toUpperCase() + link.platform?.slice(1) || 'Social',
            icon: IconComponent,
            href: link.url,
            platform: platform,
            isActive: link.isActive !== false
          }
        }).filter(link => link.isActive)
        
        setSocialLinks(transformedLinks)
      } catch (error) {
        console.error('Failed to fetch social links:', error)
        setSocialLinks([])
      } finally {
        setLoading(false)
      }
    }

    fetchSocialLinks()
  }, [])

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <img src={logo} alt="YesTimeEarn Logo" className="w-7 h-8" />
              </div>
              <div>
                <h1 className="text-xl font-bold">YesTimeEarn</h1>
                <p className="text-sm text-gray-400">Smart Investments</p>
              </div>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Revolutionizing investment with AI-powered technology, 
              transparent operations, and institutional-grade security.
            </p>
            <div className="flex space-x-4">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="w-10 h-10 bg-gray-800 rounded-lg animate-pulse"
                  />
                ))
              ) : socialLinks.length > 0 ? (
                socialLinks.map((social) => {
                  const IconComponent = social.icon
                  return (
                    <a
                      key={social.id || social.platform}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors group"
                      title={social.name}
                    >
                      <IconComponent className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                    </a>
                  )
                })
              ) : (
                <div className="text-gray-500 text-sm">
                  No social links available
                </div>
              )}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-bold mb-6">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-lg font-bold mb-6">Contact Info</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-blue-400" />
                <a 
                  href="mailto:support@yestimeearn.com"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  support@yestimeearn.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Send className="w-4 h-4 text-blue-400" />
                <a 
                  href="https://t.me/yestimeearnYTE4" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  @yestimeearnYTE4
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} YesTimeEarn. All rights reserved.
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <span>Licensed & Regulated</span>
              <span>•</span>
              <span>SSL Secured</span>
              <span>•</span>
              <span>GDPR Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer